import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { energyWorkCopy } from './energyWorkArticleContent';

const IMAGE = 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/vkqrk8xy_singing%20bowls.png';

const sources = [
  {
    "title": "Terwagne & Bush — Tibetan Singing Bowls (2011)",
    "url": "https://arxiv.org/abs/1106.6348"
  },
  {
    "title": "Effects of singing bowl exposure on sleepiness (2020)",
    "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC7263600/"
  },
  {
    "title": "Goldsby et al. — Sound Meditation, Mood and Tension (2017)",
    "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC5871151/"
  },
  {
    "title": "Does the Sound of a Singing Bowl Synchronize Meditational Brainwaves? (2023)",
    "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC10298245/"
  }
];

const copy = {
  es: {
    eyebrow: 'ABBA Massage · Artículos',
    title: 'Bienestar con intención',
    intro: 'Ideas y prácticas para entender lo que hacemos en ABBA y llevar un poco de esa pausa a tu día.',
    read: 'Leer artículo',
    back: 'Todos los artículos',
    articleTitle: '¿Por qué usamos cuencos en ABBA?',
    summary: 'Sonido, vibración y resonancia en el masaje, Reiki y Constelaciones Familiares.',
    lead: 'En ABBA, el sonido de los cuencos acompaña el cierre del masaje y las sesiones de Reiki y Constelaciones Familiares. Detrás de cada nota hay vibración, resonancia y una intención de cuidado.',
    sections: [
  {
    "title": "¿Cómo “canta” un cuenco?",
    "paragraphs": [
      "Al golpear suavemente un cuenco con un mazo, sus paredes oscilan en movimientos muy pequeños. Esa vibración mueve el aire y genera las ondas sonoras que llegan a tus oídos.",
      "Al recorrer el borde con una baqueta, la fricción aporta energía de manera continua y mantiene la vibración. Por eso el sonido puede sostenerse mientras seguimos frotando.",
      "Cada cuenco tiene distintas frecuencias naturales de vibración, relacionadas con su tamaño, forma, grosor y material. Varias pueden sonar al mismo tiempo y dar lugar a los tonos superpuestos que forman su sonido rico y envolvente."
    ],
    "source": 0
  },
  {
    "title": "¿Qué significa resonancia?",
    "paragraphs": [
      "La resonancia ocurre cuando un estímulo aporta energía cerca de una frecuencia natural de un objeto y aumenta su vibración. Es parecido a impulsar un columpio en el momento adecuado.",
      "En un cuenco con agua, el movimiento de las paredes puede producir ondas visibles en la superficie: una demostración de cómo una vibración pasa del metal al líquido. En el cuerpo, escuchar el sonido y sentir una vibración son experiencias distintas; lo que ocurre en el agua de un cuenco no describe por sí solo lo que sucede en nuestros tejidos."
    ],
    "source": 0
  },
  {
    "title": "¿Cómo percibimos esa vibración?",
    "paragraphs": [
      "Podemos percibir los cuencos como sonido y, según la cercanía y la forma de utilizarlos, también como una sensación física de vibración.",
      "Durante la sesión, el sonido ofrece un punto donde colocar tu atención. Puedes seguir una nota mientras se desvanece, notar tu respiración y regresar a ese sonido cuando aparezcan pensamientos."
    ],
    "source": 1
  },
  {
    "title": "¿Qué nos dice la investigación?",
    "paragraphs": [
      "En un estudio con 62 participantes, las personas reportaron menos tensión y fatiga después de una meditación sonora que incluyó cuencos y otros instrumentos. Estos resultados respaldan el interés por el sonido como acompañamiento de la relajación. El estudio evaluó la experiencia completa, por lo que no aisló el efecto de los cuencos."
    ],
    "source": 2
  },
  {
    "title": "¿Y las ondas cerebrales?",
    "paragraphs": [
      "Un estudio observó una respuesta de la actividad cerebral al escuchar un cuenco con pulsaciones de 6.68 Hz, dentro del rango theta.",
      "Este hallazgo abre una línea de investigación sobre la relación entre el sonido rítmico y la actividad cerebral durante la escucha. La experiencia de relajación es personal: puedes disfrutarla sin buscar un estado específico."
    ],
    "source": 3
  },
  {
    "title": "Sonido que acompaña la calma",
    "paragraphs": [
      "En ABBA vemos el efecto de los cuencos en las personas que recibimos. Por eso los integramos al cierre del masaje y como acompañamiento en las sesiones de Reiki y Constelaciones Familiares.",
      "Su sonido y su vibración crean un espacio para hacer una pausa, conectar con el cuerpo y dar lugar a lo vivido durante la sesión. Cada nota invita a escuchar, respirar y permanecer unos instantes en esa experiencia.",
      "Para nosotros, los cuencos forman parte de un cuidado diseñado con intención. Los utilizamos porque vemos su valor en las personas que acompañamos."
    ]
  }
],
    sourcesLabel: 'Fuentes y lecturas',
    sourceLabel: 'Ver fuente',
    closing: 'Antes de volver a tus pendientes, regálate unos segundos para reconocer cómo está tu cuerpo ahora. Esa pausa también es parte de tu experiencia en ABBA.'
  },
  en: {
    eyebrow: 'ABBA Massage · Articles',
    title: 'Wellness with intention',
    intro: 'Ideas and practices that explain what we do at ABBA and help you carry a little of that pause into your day.',
    read: 'Read article',
    back: 'All articles',
    articleTitle: 'Why do we use singing bowls at ABBA?',
    summary: 'Sound, vibration and resonance in massage, Reiki and Family Constellations.',
    lead: 'At ABBA, singing bowls accompany the end of massage and our Reiki and Family Constellations sessions. Behind each note are vibration, resonance and an intention to care.',
    sections: [
  {
    "title": "How does a bowl “sing”?",
    "paragraphs": [
      "When a bowl is gently struck with a mallet, its walls oscillate in tiny movements. These vibrations move the surrounding air, creating the sound waves that reach your ears.",
      "Moving a striker around the rim supplies energy through friction and sustains the vibration. This is why the sound can continue as we rub the rim.",
      "Each bowl has natural vibration frequencies related to its size, shape, thickness and material. Several can sound at once, creating the layered tones that give a bowl its rich, enveloping sound."
    ],
    "source": 0
  },
  {
    "title": "What is resonance?",
    "paragraphs": [
      "Resonance occurs when a stimulus supplies energy close to an object’s natural frequency, increasing its vibration. Think of pushing a swing at the right moment.",
      "In a water-filled bowl, the movement of the walls can create visible surface waves, demonstrating how vibration passes from metal to liquid. Hearing sound and feeling vibration are different experiences in the body; water moving inside a bowl does not, by itself, describe what happens in our tissues."
    ],
    "source": 0
  },
  {
    "title": "How do we perceive the vibration?",
    "paragraphs": [
      "We can perceive singing bowls as sound and, depending on proximity and how they are used, as a physical sensation of vibration.",
      "During a session, sound offers a place to rest your attention. You can follow a note as it fades, notice your breathing and return to the sound when thoughts arise."
    ],
    "source": 1
  },
  {
    "title": "What does research tell us?",
    "paragraphs": [
      "In a study involving 62 participants, people reported less tension and fatigue after a sound meditation that included singing bowls and other instruments. These findings support interest in sound as an accompaniment to relaxation. The study assessed the whole experience and did not isolate the effect of the bowls."
    ],
    "source": 2
  },
  {
    "title": "What about brainwaves?",
    "paragraphs": [
      "A study observed a response in brain activity while participants listened to a bowl with beats at 6.68 Hz, within the theta range.",
      "This finding opens a line of research into the relationship between rhythmic sound and brain activity during listening. Relaxation is a personal experience: you can enjoy it without trying to reach a particular state."
    ],
    "source": 3
  },
  {
    "title": "Sound that accompanies calm",
    "paragraphs": [
      "At ABBA, we see the effect of singing bowls on the people we welcome. This is why we include them at the end of massage and as an accompaniment to Reiki and Family Constellations sessions.",
      "Their sound and vibration create space to pause, connect with the body and make room for what you experienced during the session. Each note invites you to listen, breathe and stay with that experience for a few moments.",
      "For us, singing bowls are part of care designed with intention. We use them because we see their value for the people we accompany."
    ]
  }
],
    sourcesLabel: 'Sources and further reading',
    sourceLabel: 'View source',
    closing: 'Before returning to your plans, take a few seconds to notice how your body feels now. That pause is part of your ABBA experience, too.'
  }
};

export const Articles = () => {
  const { language } = useLanguage();
  const c = copy[language] || copy.en;
  return (
    <main className="min-h-screen bg-[#F8F6F3] pt-32 pb-24 px-6" data-testid="articles-page">
      <div className="max-w-6xl mx-auto">
        <p className="text-[#A91B60] uppercase tracking-[0.2em] text-sm font-semibold mb-4">{c.eyebrow}</p>
        <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-[#1C1C1C] mb-5">{c.title}</h1>
        <p className="font-['Manrope'] text-lg text-[#5A5A5A] max-w-2xl mb-12">{c.intro}</p>
        <Link to="/articles/singing-bowls" className="grid md:grid-cols-2 overflow-hidden bg-white rounded-2xl shadow-sm group">
          <img src={IMAGE} alt={language === 'es' ? 'Cuencos utilizados en ABBA Massage' : 'Singing bowls used at ABBA Massage'} className="w-full h-full min-h-72 object-cover" />
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-5">{c.articleTitle}</h2>
            <p className="font-['Manrope'] text-[#5A5A5A] leading-relaxed mb-8">{c.summary}</p>
            <span className="inline-flex items-center gap-2 text-[#A91B60] font-semibold">{c.read}<ArrowRight size={18} /></span>
          </div>
        </Link>
        <Link to="/articles/energy-work-reiki-bioenergy" className="grid md:grid-cols-2 overflow-hidden bg-white rounded-2xl shadow-sm group mt-8">
          <img src="/images/energy-healing-hands.webp" alt={language === 'es' ? 'Imagen simbólica de manos con luz dorada sobre un fondo de selva' : 'Symbolic image of hands holding golden light against a jungle background'} className="w-full h-full min-h-72 object-cover" />
          <div className="p-8 md:p-12 flex flex-col justify-center">
          <p className="text-[#75445F] uppercase tracking-[0.2em] text-sm font-semibold mb-4">Bioenergy Healing Massage</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-5">{(energyWorkCopy[language] || energyWorkCopy.en).title}</h2>
          <p className="font-['Manrope'] text-[#5A5A5A] leading-relaxed mb-8">{(energyWorkCopy[language] || energyWorkCopy.en).summary}</p>
          <span className="inline-flex items-center gap-2 text-[#A91B60] font-semibold">{c.read}<ArrowRight size={18} /></span>
          </div>
        </Link>
      </div>
    </main>
  );
};

export const SingingBowlsArticle = () => {
  const { language } = useLanguage();
  const c = copy[language] || copy.en;
  return (
    <main className="min-h-screen bg-[#F8F6F3] pt-32 pb-24 px-6" data-testid="singing-bowls-article">
      <article className="max-w-3xl mx-auto">
        <Link to="/articles" className="inline-flex items-center gap-2 text-[#A91B60] font-['Manrope'] font-semibold mb-10"><ArrowLeft size={18} />{c.back}</Link>
        <p className="text-[#A91B60] uppercase tracking-[0.2em] text-sm font-semibold mb-4">{c.eyebrow}</p>
        <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] leading-tight mb-8">{c.articleTitle}</h1>
        <img src={IMAGE} alt={language === 'es' ? 'Cuencos de ABBA Massage' : 'ABBA Massage singing bowls'} className="w-full aspect-[16/10] object-cover rounded-2xl mb-10" />
        <div className="space-y-7 font-['Manrope'] text-lg leading-relaxed text-[#414141]">
          <p className="text-xl">{c.lead}</p>
          {c.sections.map((section, index) => (
            <section key={section.title} className="space-y-5" data-testid={`bowls-section-${index}`}>
              <h2 className="font-['Playfair_Display'] text-3xl text-[#1C1C1C] pt-4">{section.title}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {Number.isInteger(section.source) && (
                <a href={sources[section.source].url} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-[#A91B60] underline">
                  {c.sourceLabel} [{section.source + 1}]
                </a>
              )}
            </section>
          ))}
          <p className="border-l-4 border-[#A91B60] pl-6 font-['Playfair_Display'] italic text-2xl text-[#1C1C1C]">{c.closing}</p>
          <section className="border-t border-[#A91B60]/20 pt-7">
            <h2 className="font-['Playfair_Display'] text-2xl mb-5">{c.sourcesLabel}</h2>
            <ol className="list-decimal pl-6 space-y-3 text-sm">
              {sources.map((source) => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="text-[#A91B60] underline">{source.title}</a></li>
              ))}
            </ol>
          </section>
        </div>
      </article>
    </main>
  );
};

export default Articles;

